import { useTranslation } from "react-i18next";
import type { ProductContent } from "@nh/shared";
import { ProductBenefits } from "./ProductBenefits";
import { ProductBlockHeading } from "./ProductBlockHeading";
import { ProductDescription } from "./ProductDescription";

export interface ProductOverviewProps {
  /** El contenido YA resuelto en el idioma activo por `contentFor`. */
  content: ProductContent;
  /** `product.dosage`, tal como va en la etiqueta. */
  dosage: string | undefined;
  number?: string;
}

/** ¿Hay algo que contar? Si no, el bloque no existe. */
export function hasOverview(content: ProductContent, dosage?: string): boolean {
  return Boolean(
    content.tagline || content.description || content.benefits?.length || dosage,
  );
}

/**
 * "Sobre el producto": el gancho como titular, la descripción y los
 * beneficios.
 *
 * ── El titular es el GANCHO, no el nombre ────────────────────────────
 * El nombre ya es el `<h1>` del estandarte, arriba. Repetirlo aquí como
 * `<h2>` daría dos títulos idénticos seguidos y un índice de encabezados
 * que no ayuda a nadie a navegar. Cuando el producto no tiene gancho
 * capturado, el titular cae al nombre — que es repetirse, sí, pero un
 * bloque sin titular se lee como texto huérfano.
 *
 * ── Sin nada que contar, no hay bloque ───────────────────────────────
 * Ni gancho, ni descripción, ni beneficios, ni posología: la sección
 * entera desaparece en vez de dejar un "01 / Sobre el producto" con nada
 * debajo. Un título sobre un hueco grita "falta contenido"; la ausencia,
 * no.
 *
 * ── La posología va AQUÍ, con su rótulo en negrita ───────────────────
 * Es lo que hace el original: "Dosis recomendada: 2 cápsulas al día,
 * preferentemente con el desayuno.", a 14 px, justo debajo de la
 * descripción y antes de los beneficios. `product.dosage` estaba
 * guardado en el catálogo y la ficha no lo enseñaba en ningún sitio, así
 * que quien compraba no sabía cuánto tomar hasta abrir el bote.
 *
 * ── Medidas del original ─────────────────────────────────────────────
 *   rótulo        eyebrow con 14 px debajo
 *   título        clamp(28px, 3.5vw, 40px) · 300 · -0.025em
 *   descripción   20 px debajo · 15px/300 · interlínea 1.65 · ink-2
 *   posología     16 px debajo · 14px · interlínea 1.6 · ink-3
 *   beneficios    20 px debajo · píldoras mono en `mist`
 */
export function ProductOverview({ content, dosage, number }: ProductOverviewProps) {
  const { t } = useTranslation();

  if (!hasOverview(content, dosage)) return null;

  return (
    <section className="grid gap-5">
      <ProductBlockHeading
        eyebrow={t("Sobre el producto")}
        number={number}
        title={content.tagline ?? content.name}
      />

      <ProductDescription description={content.description} />

      {dosage && (
        <p className="-mt-1 text-[14px] leading-[1.6] text-ds-ink-3">
          <strong className="font-semibold text-ds-ink-2">
            {t("Dosis recomendada")}:
          </strong>{" "}
          {dosage}
        </p>
      )}

      <ProductBenefits benefits={content.benefits ?? []} />
    </section>
  );
}
